import React, { useState } from 'react';
import { saleAdd, saleRemove, saleAddCsv, saleRemoveCsv } from '../api/pricing';
import { cldThumb } from '../utils/img';

function downloadCsv(filename, headers, rows) {
  const esc = v => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [headers.join(',')];
  (rows || []).forEach(r => lines.push(headers.map(h => esc(r[h])).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const ts = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');

// Picker thumbnail sheet — same layout as the Receiving print sheet.
function pickerPdf(rows, title) {
  const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const thumb = u => cldThumb(u, 240);
  const cards = rows.map(i => {
    const img = thumb(i.image_url_1);
    const bb = i.brand_barcode ? ` (${esc(i.brand_barcode)})` : '';
    const bs = i.brand_size ? ` (${esc(i.brand_size)})` : '';
    const mrp = (i.mrp !== null && i.mrp !== undefined && i.mrp !== '') ? `₹${esc(i.mrp)}` : '';
    const l2 = `${esc(i.size || '')}${bs}${mrp ? `, ${mrp}` : ''}`;
    return `<div class="card">${img ? `<img src="${esc(img)}" />` : '<div class="noimg">no image</div>'}` +
           `<div class="l1">${esc(i.zoddle_barcode || '')}${bb}</div><div class="l2">${l2}</div></div>`;
  }).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;margin:8px}h1{font-size:12px;margin:0 0 8px}
.grid{display:grid;grid-template-columns:repeat(6,1fr);gap:5px}
.card{border:1px solid #ddd;border-radius:4px;padding:4px;text-align:center;page-break-inside:avoid}
.card img{width:100%;height:88px;object-fit:contain}
.noimg{height:88px;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:9px}
.l1{font-size:8px;font-weight:600;margin-top:2px;word-break:break-all;line-height:1.15}
.l2{font-size:8px;color:#444;line-height:1.15}
@page{margin:8mm}</style></head><body>
<h1>${esc(title)}</h1><div class="grid">${cards}</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300)};</script></body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to print.'); return; }
  w.document.write(html); w.document.close();
}

function ResultPanel({ result, kind }) {
  if (!result) return null;
  const success = result.success || [];
  const failure = result.failure || [];
  const successCols = kind === 'add'
    ? ['zoddle_barcode', 'zoddle_price', 'old_saver', 'new_saver', 'discount_pct']
    : ['zoddle_barcode', 'zoddle_price', 'old_saver', 'new_saver'];
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <p className="text-sm text-gray-700 mb-2">
        <span className="font-semibold text-zoddle-green">{success.length}</span> succeeded,{' '}
        <span className="font-semibold text-zoddle-pink">{failure.length}</span> failed.
      </p>
      <div className="flex gap-2">
        <button onClick={() => downloadCsv(`sale_${kind}_success_${ts()}.csv`, successCols, success)}
          disabled={!success.length}
          className="text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
          ⬇ Success ({success.length})
        </button>
        <button onClick={() => downloadCsv(`sale_${kind}_failure_${ts()}.csv`, ['zoddle_barcode', 'reason'], failure)}
          disabled={!failure.length}
          className="text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40">
          ⬇ Failure ({failure.length})
        </button>
        <button onClick={() => pickerPdf(success,
            `${kind === 'add' ? 'Put on sale' : 'Remove from sale'} — picker sheet (${success.length} items)`)}
          disabled={!success.length}
          className="text-xs border border-zoddle-green text-zoddle-green px-3 py-1.5 rounded-lg hover:bg-zoddle-green/5 disabled:opacity-40">
          🖨 Picker PDF ({success.length})
        </button>
      </div>
      {failure.length > 0 && (
        <div className="mt-3 max-h-40 overflow-auto text-xs">
          {failure.slice(0, 50).map((f, i) => (
            <div key={i} className="flex gap-2 py-0.5 border-b border-gray-50">
              <span className="font-mono text-gray-600 w-28">{f.zoddle_barcode || '—'}</span>
              <span className="text-zoddle-pink">{f.reason}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sale() {
  // Add single
  const [addBc, setAddBc] = useState('');
  const [addPct, setAddPct] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addFile, setAddFile] = useState(null);
  const [addResult, setAddResult] = useState(null);
  const [addBusy, setAddBusy] = useState(false);

  // Remove single
  const [remBc, setRemBc] = useState('');
  const [remFile, setRemFile] = useState(null);
  const [remResult, setRemResult] = useState(null);
  const [remBusy, setRemBusy] = useState(false);

  async function doAddSingle() {
    if (!addBc.trim()) return;
    setAddBusy(true);
    try {
      const res = await saleAdd({
        zoddle_barcode: addBc.trim(),
        sale_pct: addPct !== '' ? Number(addPct) : null,
        sale_price: addPrice !== '' ? Number(addPrice) : null,
      });
      setAddResult(res); setAddBc(''); setAddPct(''); setAddPrice('');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setAddBusy(false); }
  }
  async function doAddCsv() {
    if (!addFile) return;
    setAddBusy(true);
    try { setAddResult(await saleAddCsv(addFile)); setAddFile(null); }
    catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setAddBusy(false); }
  }
  async function doRemoveSingle() {
    if (!remBc.trim()) return;
    setRemBusy(true);
    try { setRemResult(await saleRemove(remBc.trim())); setRemBc(''); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setRemBusy(false); }
  }
  async function doRemoveCsv() {
    if (!remFile) return;
    setRemBusy(true);
    try { setRemResult(await saleRemoveCsv(remFile)); setRemFile(null); }
    catch (e) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setRemBusy(false); }
  }

  const inputCls = "border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green";

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h2 className="text-xl font-black text-gray-800 mb-1">Sale management</h2>
      <p className="text-xs text-gray-400 mb-6">
        On sale = saver price below Zoddle price. This only changes the <span className="font-semibold">saver price</span> — never the Zoddle price or the label.
        Changed items move to the Retagging queue (pill "add sale"/"remove sale") to physically add/remove the sale tag, then scan back to a bin. Only in-bin items are eligible.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Put on sale */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Put on sale</h3>
          <div className="flex flex-col gap-2 mb-4">
            <input className={inputCls} placeholder="Zoddle barcode" value={addBc} onChange={e => setAddBc(e.target.value)} />
            <div className="flex gap-2">
              <input className={inputCls + ' w-1/2'} type="number" placeholder="Sale %" value={addPct} onChange={e => setAddPct(e.target.value)} />
              <input className={inputCls + ' w-1/2'} type="number" placeholder="Sale price ₹" value={addPrice} onChange={e => setAddPrice(e.target.value)} />
            </div>
            <p className="text-[11px] text-gray-400">If both are filled, Sale % is used.</p>
            <button onClick={doAddSingle} disabled={addBusy || !addBc.trim()}
              className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
              {addBusy ? '…' : 'Put on sale'}
            </button>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400">Or upload CSV: <span className="font-mono">Zoddle_Barcode, Sale %, Sale Price</span></p>
              <button onClick={() => downloadCsv('sale_add_template.csv',
                  ['Zoddle_Barcode', 'Sale %', 'Sale Price'],
                  [{ 'Zoddle_Barcode': '1230534', 'Sale %': '40', 'Sale Price': '' },
                   { 'Zoddle_Barcode': '1230535', 'Sale %': '', 'Sale Price': '799' }])}
                className="text-[11px] text-zoddle-green hover:underline whitespace-nowrap">⬇ Template</button>
            </div>
            <div className="flex items-center gap-2">
              <input type="file" accept=".csv" onChange={e => setAddFile(e.target.files[0] || null)} className="text-xs" />
              <button onClick={doAddCsv} disabled={!addFile || addBusy}
                className="bg-zoddle-green text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50">
                {addBusy ? '…' : 'Upload'}
              </button>
            </div>
          </div>
          <ResultPanel result={addResult} kind="add" />
        </div>

        {/* Remove from sale */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Remove from sale</h3>
          <div className="flex flex-col gap-2 mb-4">
            <input className={inputCls} placeholder="Zoddle barcode" value={remBc} onChange={e => setRemBc(e.target.value)} />
            <button onClick={doRemoveSingle} disabled={remBusy || !remBc.trim()}
              className="bg-gray-700 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
              {remBusy ? '…' : 'Remove from sale'}
            </button>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400">Or upload CSV: <span className="font-mono">Zoddle_Barcode</span></p>
              <button onClick={() => downloadCsv('sale_remove_template.csv', ['Zoddle_Barcode'],
                  [{ 'Zoddle_Barcode': '1230534' }])}
                className="text-[11px] text-zoddle-green hover:underline whitespace-nowrap">⬇ Template</button>
            </div>
            <div className="flex items-center gap-2">
              <input type="file" accept=".csv" onChange={e => setRemFile(e.target.files[0] || null)} className="text-xs" />
              <button onClick={doRemoveCsv} disabled={!remFile || remBusy}
                className="bg-gray-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50">
                {remBusy ? '…' : 'Upload'}
              </button>
            </div>
          </div>
          <ResultPanel result={remResult} kind="remove" />
        </div>
      </div>
    </div>
  );
}
